import { ActionButton, ActionButtonType, Button } from "@octopusdeploy/design-system-components";
import { ProcessType } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import type * as React from "react";
import { Action, useProjectScopedAnalyticActionDispatch } from "~/analytics/Analytics";
import { isDeploymentProcessIdentifier, isProcessTemplateProcessIdentifier, isProjectTemplateProcessIdentifier, isRunbookProcessIdentifier } from "~/areas/projects/components/Process/types";
import { PermissionCheck } from "~/components/PermissionCheck";
import { processScopedEditPermission } from "../Process/Common/CommonProcessHelpers";
import { useProcessContext } from "../Process/Contexts/ProcessContext";
import { useProcessQueryStringContext } from "../Process/Contexts/ProcessQueryString/ProcessQueryStringContext";
type AddRunbookStepButtonProps = {
    projectId: string;
};
const AddRunbookStepButton: React.FC<AddRunbookStepButtonProps> = ({ projectId }) => {
    const processQueryStringContext = useProcessQueryStringContext();
    const dispatchAction = useProjectScopedAnalyticActionDispatch(projectId);
    return (<PermissionCheck permission={processScopedEditPermission(ProcessType.Runbook)} project={projectId} wildcard={true}>
            <ActionButton type={ActionButtonType.Secondary} label="Add Step" onClick={() => {
            processQueryStringContext.actions.showProcessStepTemplates();
            dispatchAction("Add Deployment Step", { resource: "Runbook Process", action: Action.Add });
        }}/>
        </PermissionCheck>);
};
AddRunbookStepButton.displayName = "AddRunbookStepButton"
type AddDeploymentsStepButtonProps = {
    projectId: string;
};
const AddDeploymentsStepButton: React.FC<AddDeploymentsStepButtonProps> = ({ projectId }) => {
    const processQueryStringContext = useProcessQueryStringContext();
    const dispatchAction = useProjectScopedAnalyticActionDispatch(projectId);
    return (<PermissionCheck permission={processScopedEditPermission(ProcessType.Deployment)} project={projectId} wildcard={true}>
            <ActionButton type={ActionButtonType.Secondary} label="Add Step" onClick={() => {
            processQueryStringContext.actions.showProcessStepTemplates();
            dispatchAction("Add Deployment Step", { resource: "Deployment Process", action: Action.Add });
        }}/>
        </PermissionCheck>);
};
AddDeploymentsStepButton.displayName = "AddDeploymentsStepButton"
type AddProcessTemplatesStepButtonProps = {
    buttonType: ActionButtonType;
};
const AddProcessTemplatesStepButton: React.FC<AddProcessTemplatesStepButtonProps> = ({ buttonType }) => {
    const processQueryStringContext = useProcessQueryStringContext();
    return (<ActionButton type={buttonType} label="Add Step" onClick={() => {
            processQueryStringContext.actions.showProcessStepTemplates();
        }}/>);
};
AddProcessTemplatesStepButton.displayName = "AddProcessTemplatesStepButton"
type AddProjectTemplatesStepButtonProps = {
    slug: string;
    gitRef: string;
};
const AddProjectTemplatesStepButton: React.FC<AddProjectTemplatesStepButtonProps> = ({ slug, gitRef }) => {
    return <Button importance="primary" label={"Add Step"} href={links.projectTemplateDeploymentProcessEditorPage.generateUrl({ slug, gitRef }, { stepTemplates: true })}/>;
};
AddProjectTemplatesStepButton.displayName = "AddProjectTemplatesStepButton"
const ContextAddStepButton: React.FC = () => {
    const processContext = useProcessContext();
    const processIdentifier = processContext.state.processIdentifier;
    if (isDeploymentProcessIdentifier(processIdentifier)) {
        return <AddDeploymentsStepButton projectId={processIdentifier.projectId}/>;
    }
    if (isRunbookProcessIdentifier(processIdentifier)) {
        return <AddRunbookStepButton projectId={processIdentifier.projectId}/>;
    }
    if (isProcessTemplateProcessIdentifier(processIdentifier)) {
        return <AddProcessTemplatesStepButton buttonType={ActionButtonType.Secondary}/>;
    }
    if (isProjectTemplateProcessIdentifier(processIdentifier)) {
        return <AddProjectTemplatesStepButton slug={processIdentifier.slug} gitRef={processIdentifier.gitRef}/>;
    }
    throw new Error(`Unhandled process identifier ${processIdentifier}`);
};
ContextAddStepButton.displayName = "ContextAddStepButton"
export default ContextAddStepButton;
